import React, { useState, useReducer, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import AsyncSelect from 'react-select/async';
import ReactSelect from 'react-select';
import moment from 'moment-timezone';

import DeleteModal from '../../Modal/DeleteModal';
import DateAndTimePicker from '../../Form/DateAndTimePicker';

import slugify from '../../../utils/slugify';
import LanguageTabsForms from '../../Tabs/LanguageTabsForms';

import Toggle from '../DragAndDrop/ItemsComponents/ui/toggle';
import UIImage from '../DragAndDrop/ItemsComponents/ui/UIImage';

import useArtistList from '../../../hooks/useArtistList';
import { transformDataToSelectOptions, getSlugNamesByCollection } from '../../../utils/selectHandlers';
import isValidSlugName from '../../../utils/isValidSlugName';
import TIMEZONES from '../../../utils/timezones';
import reducer from '../../../store/formReducer';
import { init, setError, setValue, setValueLocalized, clearError } from '../../../store/actions';

// TODO: add proptypes and move subcomponents to single files
const initialState = {
  isEdit: false,
  error: {},
  data: {
    artistName: '',
    artistSetName: '',
    artistSetReadableName: {},
    artistSlugName: '',
    artistSetSlugName: '',
    artistReadableName: {},
    artistSetGenres: [],
    countryCode: '',
    dateTimeStartReal: null,
    dateTimeStartHuman: null,
    duration: 0,
    image: {},
    mediaLinks: {
      itunes: '',
      video: '',
      web: '',
      disco: '',
    },
    postUri: '',
    shortTitle: {},
    show: false,
    showDate: false,
    showDateTime: false,
    showTicket: false,
    showVenue: false,
    smallText: {},
    venueSlugName: '',
    dateTimeEndReal: null,
    stock: 0,
    timezone: 'Europe/Madrid',
  },
};

const useArtistSet = (event) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { data } = state;

  useEffect(() => {
    if (event) {
      init(dispatch)(event);
    }
  }, []);

  const checkValidSlugName = async () => {
    if (!data.artistSetName) return;
    const isValid = await isValidSlugName(data.artistSetName, 'artistSets');
    if (!isValid) {
      setError(dispatch)({
        artistSetName: 'This artistSetName already exists, try a new one',
      });
      return;
    }
    clearError(dispatch)('artistSetSlugName');
    setValue(dispatch)('artistSetSlugName', slugify(data.artistSetName).toLowerCase());
  };

  return { state, dispatch, checkValidSlugName };
};

export const Input = ({ error, label, name, onChange, type, value, ...otherProps }) => (
  <div className="w-full">
    <label className="flex flex-col font-americaMonoRegular text-xs uppercase" htmlFor={name}>
      {label}
      <input
        id={name}
        onChange={onChange}
        name={name}
        type={type || 'text'}
        value={value || ''}
        className={`mt-2 rounded-full border-1 p-2 bg-white outline-none ${error ? ' text-red border-red border-2' : ' border-black text-black '}`}
        {...otherProps}
      />
    </label>
  </div>
);

export const Textarea = ({ label, name, onChange, value, ...otherProps }) => (
  <div className="w-full">
    <label className="flex flex-col font-americaMonoRegular text-xs uppercase" htmlFor={name}>
      {label}
      <textarea className="font-americaMonoRegular p-2 bg-white outline-none w-full mt-2 rounded-lg h-28" onChange={onChange} name={name} type="text" value={value} {...otherProps} />
    </label>
  </div>
);

export const Spacer = () => <div className="m-4" />;

const SelectAsync = ({ className, defaultOptions, isLoading, onChange, label, loadOptions, name, value, isMulti }) => (
  <div className={`w-full ${className}`}>
    <label className="font-americaMonoRegular uppercase text-xs" htmlFor={name}>
      {label}
      <AsyncSelect
        className="font-americaMonoRegular text-black font-bold mt-1"
        isMulti={isMulti}
        id={name}
        name={name}
        cacheOptions
        value={value}
        isLoading={isLoading}
        loadOptions={loadOptions}
        defaultOptions={defaultOptions}
        onChange={onChange}
      />
    </label>
  </div>
);

export const Select = ({ className, onChange, label, options, name, value, isMulti }) => (
  <div className={`w-full ${className}`}>
    <label className="font-americaMonoRegular uppercase text-xs" htmlFor={name}>
      {label}
      <ReactSelect
        className="font-americaMonoRegular text-black font-bold mt-1"
        isMulti={isMulti}
        id={name}
        name={name}
        value={value}
        options={options}
        onChange={onChange}
      />
    </label>
  </div>
);

const getDateWithTimezone = (dateWithoutUtc, timezone) => moment.tz(dateWithoutUtc, timezone).format();

const getDateStrippedUtc = (date, timezone) => {
  let dater = date;
  const isTimestamp = !Number.isNaN(Number(date));

  if (isTimestamp) dater = Number(date);

  if (timezone) return moment.tz(dater, timezone).format('YYYY-MM-DDTHH:mm:ss');
  return moment(dater).format('YYYY-MM-DDTHH:mm:ss');
};

// NOTE: dateNoUtc is used by date-picker
// NOTE: dateUtc is the value we save on the DB
const useDateUtc = (initialDate, timezone) => {
  const [dateNoUtc, setDateNoUtc] = useState();
  const [dateUtc, setDateUTC] = useState();

  useEffect(() => {
    if (initialDate && !dateNoUtc) {
      const strippedTimezone = getDateStrippedUtc(initialDate, timezone);
      setDateNoUtc(strippedTimezone);
    } else setDateUTC(getDateWithTimezone(dateNoUtc, timezone));
  }, [initialDate, timezone, dateNoUtc]);

  return [dateNoUtc, dateUtc, setDateNoUtc];
};

const Form = ({ canItemBeDeleted, formInput, handleDelete, saveForm, handleClick }) => {
  const { checkValidSlugName, state: { error, data, isEdit }, dispatch } = useArtistSet(formInput);
  console.log("🚀 ~ Form ~ error:", error)
  const artistList = useArtistList();
  const [dateRealNoUtc, dateRealUtc, setDateRealNoUtc] = useDateUtc(data.dateTimeStartReal, data.timezone);
  const [dateHumanlNoUtc, dateHumanlUtc, setDateHumanlNoUtc] = useDateUtc(data.dateTimeStartHuman, data.timezone);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (dateRealUtc) setValue(dispatch)('dateTimeStartReal', dateRealUtc);
  }, [dateRealUtc]);

  useEffect(() => {
    if (dateHumanlUtc) setValue(dispatch)('dateTimeStartHuman', dateHumanlUtc);
  }, [dateHumanlUtc]);

  const validateForm = () => {
    const errors = {};

    if (!data.artistSetName) {
      errors.artistSetName = 'Artist Set Name is required';
    }
    if (!data.artistSetSlugName) {
      errors.artistSetSlugName = 'Artist Set Slug Name is required';
    }
    if (!data.artistSetReadableName?.en) {
      errors.artistSetReadableName = 'Artist Set Readable Name in English is required';
    }

    setError(dispatch)(errors);
    return Object.keys(errors).length === 0;
  };

  const clearFieldError = (fieldName) => {
    clearError(dispatch)(fieldName);
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    if (error[name]) clearFieldError(name);
    setValue(dispatch)(name, value);
  };

  const handleInputNumber = (e) => {
    const { name, value } = e.target;
    setValue(dispatch)(name, Number(value));
  };

  const handleToggle = (e) => {
    const { name, checked } = e.target;
    setValue(dispatch)(name, checked);
  };

  const handleSelect = (name, value) => {
    setValue(dispatch)(name, value.value);
    // NOTE: programatically set the artist name
    if (name === 'artistSlugName') {
      setValue(dispatch)('artistName', value.label);
      setValue(dispatch)('postUri', value.value);
    }
  };

  const handleLocalizedInput = language => (e) => {
    const { name, value } = e.target;
    if (error[name]) clearFieldError(name);
    setValueLocalized(dispatch)(name, value, language);
  };

  const handleDate = (name, value) => {
    const utcStrippedDate = getDateStrippedUtc(value);
    if (name === 'dateTimeStartReal') setDateRealNoUtc(utcStrippedDate);
    else setDateHumanlNoUtc(utcStrippedDate);
  };

  const handleImage = language => (imageUrl) => {
    setValueLocalized(dispatch)('image', imageUrl, language);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const action = isEdit ? 'modify' : 'create';
      saveForm(data, action);
      handleClick(action);
    }
  };
  const handleDeleteEvent = () => {
    handleDelete(data?._id, data?.eventSlugName);
  };

  const hasErrors = Object.keys(error).length > 1;
  
  return (
    <form onSubmit={handleSubmit} className="bg-black text-white p-4">
      {
        !isEdit ? <Input name="artistSetName" error={error.artistSetName} onChange={handleInput} onBlur={checkValidSlugName} label="Artist Set Name" value={data?.artistSetName} required /> : null
      }
      <Spacer />
      <div className="font-americaMonoRegular text-xs mb-4 mt-2">
        Artist Set Name:
        {' '}
        {`/${data?.artistSetSlugName || ''}`}
      </div>
      <SelectAsync
        className="mb-2"
        name="venueSlugName"
        label="Venue"
        value={transformDataToSelectOptions(data?.venueSlugName)}
        loadOptions={inputValue => getSlugNamesByCollection('venues', inputValue)}
        onChange={value => handleSelect('venueSlugName', value)}
      />
      <Spacer />
      <Select
        className="mb-2"
        name="artistSlugName"
        label="Artist"
        value={transformDataToSelectOptions(data?.artistSlugName)}
        options={artistList}
        onChange={value => handleSelect('artistSlugName', value)}
      />
      <Spacer />
      <LanguageTabsForms description="ee" title="aa" activeTabStyle="tab active" width="w-full">
        {(activeLanguage, languages) => (
          <div className="bg-greyWithinForm px-4 xxl1:pr-2 py-1 mb-3 rounded-b-lg mr-1 xxl:mr-0">
            {languages.map(lang => (
              <div key={lang} className={`${activeLanguage.toLowerCase() === lang.toLowerCase() ? 'flex flex-nowrap mx-1 flex-row' : 'hidden'}`}>
                <div className="w-1/2 xxl1:w-4/5">
                  <Spacer />
                  <Input name="artistSetReadableName" onChange={handleLocalizedInput(lang)} label="Artist Set Readable Name" value={data?.artistSetReadableName?.[lang]} required={lang === 'en'} error={error.artistSetReadableName} />
                  <Spacer />
                  <Input name="shortTitle" onChange={handleLocalizedInput(lang)} label="Short Title" value={data?.shortTitle?.[lang]} />
                  <Spacer />
                  <Textarea name="smallText" onChange={handleLocalizedInput(lang)} label="Small Text" value={data.smallText?.[lang]} />
                </div>
                <div className="w-1/2 pl-4">
                  <UIImage activeLanguage={lang} getImageUrl={handleImage(lang)} imageUrl={data.image || {}} />
                </div>
              </div>
            ))}
          </div>
        )}
      </LanguageTabsForms>
      <Spacer />
      <div className="w-full flex">
        <div className="w-1/2 pr-2">
          <Input name="countryCode" onChange={handleInput} label="Country Code" value={data.countryCode} />
        </div>
        <div className="w-1/2 pl-2">
          <Input name="genre" onChange={handleInput} label="Genre (space bar between words for multiple records)" value={data.genre} />
        </div>
      </div>
      <Spacer />
      <Select
        className="mb-2 w-1/2"
        name="timezone"
        label="Timezone"
        value={transformDataToSelectOptions(data?.timezone)}
        options={TIMEZONES}
        onChange={value => handleSelect('timezone', value)}
      />
      <div className="w-full flex">
        <DateAndTimePicker title="Begin Time" name="dateTimeStartReal" handleDate={handleDate} existingDate={dateRealNoUtc} action className="w-full pr-2" />
        <DateAndTimePicker title="Lineup Day" name="dateTimeStartHuman" handleDate={handleDate} existingDate={dateHumanlNoUtc} action className="w-full pl-2" />
        <div className="w-1/3 pl-4 py-4">
          <Input name="duration" onChange={handleInputNumber} label="Duration" value={data.duration} type="number" />
        </div>
      </div>
      <div className="flex w-full">
        <div className="w-1/2 pr-2">
          <Input name="itunes" onChange={handleInput} label="Itunes Media Link" value={data.itunes} />
        </div>
        <div className="w-1/2 pl-2">
          <Input name="video" onChange={handleInput} label="Video Media Link" value={data.video} />
        </div>
      </div>
      <Spacer />
      <div className="flex w-full">
        <div className="w-1/2 pr-2">
          <Input name="web" onChange={handleInput} label="Web Media Lin" value={data.web} />
        </div>
        <div className="w-1/2 pl-2">
          <Input name="disco" onChange={handleInput} label="Disco Media Link" value={data.disco} />
        </div>
      </div>
      <Spacer />
      <div className="flex w-full">
        <div className="w-1/2 pr-2">
          <Input name="stock" onChange={handleInputNumber} label="Stock" value={data.stock} type="number" />
        </div>
      </div>
      <Spacer />
      <div className="flex w-full justify-around mb-6">
        <Toggle title="Show Artist Set" value={data?.show} callback={handleToggle} name="show" />
      </div>
      <div className="flex justify-around my-2">
        {
          isEdit ? (
            <button type="button" className="bg-red text-white font-americaMonoBold text-xs px-6 py-2 rounded-full uppercase ml-7 mt-2 w-110px text-black rounded-full py-2 px-4" onClick={() => setShowDeleteModal(true)}>
              Delete
            </button>
          ) : null
        }
        <button onClick={handleSubmit} className={` font-americaMonoBold text-xs px-6 py-2 rounded-full  uppercase ml-7 mt-2 w-110px text-black rounded-full py-2 px-4 bg-${hasErrors ? 'gray' : 'white'}`} type="submit" disabled={hasErrors}>
          {`${(isEdit ? 'Save' : 'Create')}`}
        </button>
      </div>
      {showDeleteModal ? <Delete canItemBeDeleted={canItemBeDeleted} onAccept={handleDeleteEvent} onClose={() => setShowDeleteModal(false)} /> : null}
    </form>
  );
};

const deleteOptions = (canItemBeDeleted, onAccept, onClose) => {
  if (!canItemBeDeleted) {
    return ({
      title: 'Item cannot be deleted',
      description: 'This event has other records linked to it and it cannot be deleted.',
      handleClick: onClose,
      closeToggleDelete: onClose,
    });
  }
  return ({ title: 'Item to be deleted', description: 'Are you sure you want to delete this event?', handleClick: onAccept, closeToggleDelete: onClose });
};

export const Delete = ({ canItemBeDeleted, onAccept, onClose }) => {
  const { title, description, handleClick, closeToggleDelete } = deleteOptions(canItemBeDeleted, onAccept, onClose);
  return (
    <DeleteModal zIndex="20" title={title} handleClick={handleClick} closeToggleDelete={closeToggleDelete}>
      {description}
    </DeleteModal>
  );
};

export default Form;
